import { Link } from 'react-router';
import { motion } from 'motion/react';
import { GAME_LABELS } from '@redpandev/shared';
import { Mascot, spring, useReducedMotion } from '@redpandev/ui';
import { useAuth } from '../auth/AuthContext';
import { AmbientBackdrop } from '../components/hub/AmbientBackdrop';
import { DailyCover } from '../components/hub/DailyCover';
import { GameCard } from '../components/hub/GameCard';
import { GriffonneCover } from '../components/hub/GriffonneCover';
import { HubGrid } from '../components/hub/HubGrid';
import { MotusCover } from '../components/hub/MotusCover';
import { PandaPanicCover } from '../components/hub/PandaPanicCover';
import { pillPrimary, pillSecondary } from '../components/hub/linkStyles';
import { isPandaPanicOnlineEnabled } from '../games/panda-panic/flag';
import { LEGAL_LINKS } from './legal/LegalLayout';

/** Teaser : cadenas + étoiles qui scintillent doucement. */
function LockedCover() {
  return (
    <div className="relative flex h-28 items-center justify-center overflow-hidden rounded-lg border border-ink-600/40 bg-app/60">
      <svg
        viewBox="0 0 24 24"
        className="h-12 w-12 text-ink-400"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        aria-hidden="true"
      >
        <rect x="5" y="11" width="14" height="9" rx="2.5" fill="currentColor" fillOpacity="0.15" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" strokeLinecap="round" />
        <circle cx="12" cy="15.5" r="1.4" fill="currentColor" stroke="none" />
      </svg>
      {[
        { className: 'left-6 top-4 h-3 w-3', delay: '0s' },
        { className: 'right-8 top-7 h-2.5 w-2.5', delay: '0.6s' },
        { className: 'right-14 bottom-4 h-2 w-2', delay: '1.1s' },
      ].map((star) => (
        <svg
          key={star.className}
          viewBox="0 0 24 24"
          aria-hidden="true"
          className={`absolute ${star.className} text-sun-400/80 motion-safe:animate-pulse-soft`}
          style={{ animationDelay: star.delay }}
        >
          <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" fill="currentColor" />
        </svg>
      ))}
    </div>
  );
}

export function HomePage() {
  const { status, user } = useAuth();
  const reduced = useReducedMotion();

  return (
    <main className="relative overflow-hidden">
      <AmbientBackdrop />
      <div className="relative mx-auto flex max-w-content flex-col items-center px-4 pb-16 pt-10 sm:px-6 lg:pt-14">
        {/* ─── Héro ─────────────────────────────────────────────── */}
        <header className="flex flex-col items-center text-center">
          {/* Entrée pop (Framer) et flottement idle (CSS) sur des nœuds séparés
              pour ne jamais animer le même transform avec deux systèmes. */}
          <div className="motion-safe:animate-float" style={{ animationDuration: '5s' }}>
            <motion.div
              initial={{ scale: 0, rotate: -12 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={spring.soft}
            >
              <Mascot mood="excited" size={132} aria-label="Redpan, la mascotte panda roux" />
            </motion.div>
          </div>
          <motion.h1
            className="mt-4 bg-gradient-to-r from-panda-500 via-panda-600 to-grape-500 bg-clip-text font-display text-6xl font-bold tracking-tight text-transparent sm:text-display"
            style={{ backgroundSize: '200% 100%', backgroundPosition: '0% 50%' }}
            animate={reduced ? undefined : { backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
          >
            RedPanDev
          </motion.h1>
          <p className="mt-3 max-w-md text-lg text-ink-100/80 sm:text-xl">
            Des jeux multijoueurs, réactifs&nbsp;— et un peu fous.
          </p>

          {status === 'authenticated' && user ? (
            <div className="mt-6 flex flex-col items-center gap-4">
              <p className="text-lg text-ink-400">
                Salut{' '}
                <span className="font-display text-xl font-bold text-panda-500">
                  {user.displayName}
                </span>{' '}
                !
              </p>
              <Link to="/motus" className={pillPrimary}>
                Jouer à Motus duel
              </Link>
            </div>
          ) : status === 'anonymous' ? (
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <Link to="/signup" className={pillPrimary}>
                Créer un compte
              </Link>
              <Link to="/login" className={pillSecondary}>
                Connexion
              </Link>
              {/* Produit d'appel (ADR 0012 §5) : le mot du jour se joue SANS compte. */}
              <Link to="/motus-daily" className={pillSecondary}>
                <span aria-hidden="true">🎯&nbsp;</span>Le mot du jour — joue sans compte&nbsp;!
              </Link>
              {/* Griffonne aussi jouable sans compte (invité, ADR 0013). */}
              <Link to="/griffonne" className={pillSecondary}>
                <span aria-hidden="true">🎨&nbsp;</span>Griffonne — dessine & devine
              </Link>
            </div>
          ) : null}
        </header>

        {/* ─── Jeux ─────────────────────────────────────────────── */}
        <HubGrid className="mt-12 sm:mt-16" label="Jeux disponibles">
          <GameCard
            title="Motus duel"
            description="Devine le mot avant la fin de la grille — duel en temps réel où chaque lettre compte."
            badge="2 joueurs · temps réel"
            to="/motus"
            cta="Jouer"
            cover={<MotusCover />}
          />
          <GameCard
            title={GAME_LABELS['motus-daily']}
            description="Un mot secret, le même pour tout le monde, six essais — et une seule partie par jour. Tu le trouveras en combien de coups ?"
            badge="Solo · 1 mot par jour"
            to="/motus-daily"
            cta="Jouer"
            cover={<DailyCover />}
          />
          <GameCard
            title={GAME_LABELS.griffonne}
            description="Dessine, devine, marque des points ! Un joueur croque un mot au feutre, les autres tapent leur réponse — plus tu trouves vite, plus tu marques."
            badge="Jusqu’à 8 · sans compte"
            to="/griffonne"
            cta="Jouer"
            cover={<GriffonneCover />}
          />
          {/* Panda Panic : carte visible SEULEMENT quand le multijoueur est activé
              (serveur autoritaire déployé) — sinon la prod reste intacte (spec 15 §5). */}
          {isPandaPanicOnlineEnabled() && (
            <GameCard
              title={GAME_LABELS['panda-panic']}
              description="Quatre pandas, une plateforme qui s'effondre : bouscule, esquive les rochers et reste le dernier debout. Chaos garanti."
              badge="Jusqu’à 4 · 3D en ligne"
              to="/panda-panic/online"
              cta="Jouer"
              cover={<PandaPanicCover />}
            />
          )}
          <GameCard
            comingSoon
            title="???"
            description="Encore sous cadenas — les étoiles savent, mais elles ne diront rien."
            badge="Bientôt"
            cover={<LockedCover />}
          />
        </HubGrid>

        <footer className="mt-16 flex flex-col gap-4 text-xs text-ink-400">
          <div className="flex items-center gap-2">
            <Mascot size={22} animated={false} />
            <span>redpan.dev — poussé à la patte par un panda roux suralimenté en bambou.</span>
          </div>
          <nav aria-label="Liens légaux" className="flex flex-wrap gap-x-4 gap-y-2">
            {LEGAL_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="underline underline-offset-2 transition-colors duration-fast ease-fun hover:text-panda-500"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </footer>
      </div>
    </main>
  );
}
